import {Dispatch, FC, SetStateAction} from 'react';

import {DialogContent, IconButton, Box, Typography} from '@mui/material';
import {t} from 'i18next';
import {Trans} from 'react-i18next';

import {CloseIcon} from 'assets/icons/closeIcon';
import {BasketProductImage} from 'components/basket-product-image/BasketProductImage';
import {ProductType} from 'types';
import {formatPrice, getCurrencySymbol} from 'utils/currency';
import DiscountPriceBox from 'views/discount-price-box/DiscountPriceBox';

import {
  ModalContent,
  StyledCardContentWrapper,
  StyledDialogTitle,
  StyledCardContent,
  StyledModal,
  StyledTitleTypography,
  StyledContetTypography,
  StyledPriceContainer,
  StyledPriceTypography,
  UpchargeInfoContainer,
  StyledUpTitleTypography
} from './styles';

interface UpchargePopupProps {
  showUpchargeModal: boolean;
  setShowUpchargeModal: Dispatch<SetStateAction<boolean>>;
  coverImageURL: string;
  title: string;
  weight: string;
  priceUnit: string;
  productType: ProductType;
  originPrice: number;
  displayPrice: number;
  promotedPrice?: number;
  upchargePercentage?: number;
  upchargeMinimumThreshold?: number;
}

const InfoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 16 16" enable-background="new 0 0 16 16">
    <circle fill="#FFF" cx="20" cy="20" r="20" />
    <path
      fill="#1374e6"
      d="M8,0C3.6,0,0,3.6,0,8s3.6,8,8,8s8-3.6,8-8S12.4,0,8,0z M8,13c-0.5,0-1-0.5-1-1s0.5-1,1-1s1,0.5,1,1
	S8.5,13,8,13z M9,9H7V3h2V9z"
    />
  </svg>
);

export const UpchargePopup: FC<UpchargePopupProps> = ({
  showUpchargeModal,
  setShowUpchargeModal,
  title,
  coverImageURL,
  priceUnit,
  originPrice,
  displayPrice,
  weight,
  promotedPrice,
  productType,
  upchargePercentage,
  upchargeMinimumThreshold
}: UpchargePopupProps) => {
  return (
    <>
      <StyledModal open={showUpchargeModal} onClose={() => setShowUpchargeModal(!showUpchargeModal)}>
        <StyledDialogTitle>
          <StyledUpTitleTypography>{t('upcharge.priceUpcharged')}</StyledUpTitleTypography>
          <IconButton
            aria-label="close"
            onClick={() => setShowUpchargeModal(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[900]
            }}
          >
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>
        <DialogContent>
          <ModalContent>
            <StyledCardContentWrapper role="button" tabIndex={0} onKeyDown={() => null}>
              <BasketProductImage src={coverImageURL} productType={productType} />
              <StyledCardContent>
                <StyledTitleTypography color="secondary" gutterBottom variant="body1" component="p">
                  <b>{title}</b>
                </StyledTitleTypography>
                <Typography display={'block'} variant="caption" color="text.secondary">
                  {weight}
                </Typography>
                <DiscountPriceBox
                  originPrice={originPrice}
                  priceUnit={priceUnit}
                  displayPrice={promotedPrice ? promotedPrice : displayPrice}
                />
              </StyledCardContent>
            </StyledCardContentWrapper>
          </ModalContent>
          <StyledContetTypography>
            <Trans
              i18nKey={t('upcharge.message', {
                minimumThreshold: upchargeMinimumThreshold,
                percentage: upchargePercentage
              })}
              components={{
                b: <b />,
                span: <span />
              }}
            />
            <StyledPriceContainer>
              <Box sx={{display: 'flex'}}>
                {t('upcharge.regularPrice')}:
                <StyledPriceTypography>
                  {formatPrice(originPrice)} {getCurrencySymbol()}
                </StyledPriceTypography>
              </Box>
              <Box sx={{display: 'flex'}}>
                {t('upcharge.upchargedPrice')}:
                <StyledPriceTypography>
                  {formatPrice(promotedPrice)} {getCurrencySymbol()}
                </StyledPriceTypography>{' '}
              </Box>
            </StyledPriceContainer>
            <UpchargeInfoContainer>
              <Box marginRight={1}>
                <InfoIcon />
              </Box>
              <Box margin={2}>
                <Typography>{t('upcharge.infoMessages.1')}</Typography>
                <Typography> {t('upcharge.infoMessages.2')}</Typography>
                <Typography>{t('upcharge.infoMessages.3')}</Typography>
              </Box>
            </UpchargeInfoContainer>
          </StyledContetTypography>
        </DialogContent>
      </StyledModal>
    </>
  );
};
